class ProductStickyForm extends HTMLElement {
  constructor() {
    super();
    this.optionSelectors = this.querySelectorAll('select[data-option-index]');
    this.productData = this._getProductData();
    this.currentVariant = null;

    this.imageWrapper = this.querySelector('.product-sticky-form__image-wrapper');
    this.priceElement = this.querySelector('.product-sticky-form__price');
    this.stickyAddToCartButton = this.querySelector('#StickyAddToCart');
    this._firstScreenHeight = window.innerHeight;
    this._isMainButtonVisible = true;
    this._isFooterVisible = false;
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onOptionChange = this._onOptionChange.bind(this);
    this.handleStickyAddToCart = this.handleStickyAddToCart.bind(this);

    this.optionSelectors.forEach(selector => {
      selector.addEventListener('change', this._onOptionChange);
    });

    if (this.stickyAddToCartButton) this.stickyAddToCartButton.addEventListener('click', this.handleStickyAddToCart);
    this._setupVisibilityObserver();
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
    this._preventInitialBroadcast = true;
    this._onOptionChange();
    this._preventInitialBroadcast = false;
    this._activeSizeTab = window.activeSizeTab || 'us-women';
    this._syncSizeOptionLabels();
    window.addEventListener('sizeTabChange', (e) => {
      const d = e.detail || {};
      const t = d.tab || 'us-women';
      this._activeSizeTab = t;
      this._syncSizeOptionLabels();
      this._updateUI();
    });
    this._isExternalUpdate = false;
    window.addEventListener('variantOptionsChange', (e) => {
      const opts = (e.detail && e.detail.options) || [];
      this._isExternalUpdate = true;
      this._applyOptions(opts);
      this._isExternalUpdate = false;
    });
  }

  disconnectedCallback() {
    this.optionSelectors.forEach(selector => {
      selector.removeEventListener('change', this._onOptionChange);
    });
    if (this.stickyAddToCartButton) this.stickyAddToCartButton.removeEventListener('click', this.handleStickyAddToCart);
    if (this.observer) this.observer.disconnect();
    if (this.footerObserver) this.footerObserver.disconnect();
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
  }

  _getProductData() {
    const script = document.getElementById(`ProductJson-${this.dataset.section}`);
    if (script) {
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.error("Error parsing product JSON", e);
        return null;
      }
    }
    return null;
  }

  _onOptionChange() {
    const selectedOptions = Array.from(this.optionSelectors).map(select => select.value);
    this.currentVariant = this._findVariant(selectedOptions);
    this._updateUI();
    if (!this._preventInitialBroadcast && !this._isExternalUpdate) {
      window.dispatchEvent(new CustomEvent('stickyVariantOptionsChange', { detail: { options: selectedOptions } }));
    }
  }

  _findVariant(selectedOptions) {
    if (!this.productData || !this.productData.variants) return null;
    return this.productData.variants.find(variant => {
      return selectedOptions.every((optionValue, index) => {
        return variant.options[index] === optionValue;
      });
    });
  }

  _updateUI() {
    if (!this.currentVariant) {
        if(this.stickyAddToCartButton) {
            this.stickyAddToCartButton.disabled = true;
            this.stickyAddToCartButton.textContent = 'Unavailable';
        }
        return;
    }

    // Update Image
    if (this.currentVariant.featured_image && this.imageWrapper) {
        const img = this.imageWrapper.querySelector('img');
        if (img) {
            img.src = this.currentVariant.featured_image.src;
            img.srcset = ''; // Or construct a proper srcset if needed
        }
    }

// Update Price + Compare-at Price (strike-through)
    if (this.priceElement) {
        const formatMoney = (cents, format) => {
            if (typeof cents == 'string') {
                cents = cents.replace('.', '');
            }
            let value = '';
            const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
            const formatString = (format || this.productData.shop.money_format);

            function defaultOption(opt, def) {
                return (typeof opt == 'undefined' ? def : opt);
            }

            function formatWithDelimiters(number, precision, thousands, decimal) {
                precision = defaultOption(precision, 2);
                thousands = defaultOption(thousands, ',');
                decimal = defaultOption(decimal, '.');

                if (isNaN(number) || number == null) {
                    return 0;
                }

                number = (number / 100.0).toFixed(precision);

                let parts = number.split('.'),
                    dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
                    centsValue = parts[1] ? (decimal + parts[1]) : '';

                return dollars + centsValue;
            }

            switch (formatString.match(placeholderRegex)[1]) {
                case 'amount':
                    value = formatWithDelimiters(cents, 2);
                    break;
                case 'amount_no_decimals':
                    value = formatWithDelimiters(cents, 0);
                    break;
                case 'amount_with_comma_separator':
                    value = formatWithDelimiters(cents, 2, '.', ',');
                    break;
                case 'amount_no_decimals_with_comma_separator':
                    value = formatWithDelimiters(cents, 0, '.', ',');
                    break;
            }

            return formatString.replace(placeholderRegex, value);
        };

        const moneyFormat = this.productData.shop.money_with_currency_format || this.productData.shop.money_format;
        const salePrice = formatMoney(this.currentVariant.price, moneyFormat);

        const compareAt = this.currentVariant.compare_at_price;
        const shouldShowCompare = !!compareAt && compareAt > this.currentVariant.price;
        let compareHtml = '';
        if (shouldShowCompare) {
            const comparePrice = formatMoney(compareAt, moneyFormat);
            compareHtml = `<span data-product-price-compare class="compare_at_price" style="color: #666; text-decoration: line-through;">${comparePrice}</span>`;
        }

        // Render sale price and optional compare-at price inside the container
        this.priceElement.innerHTML = `${salePrice}${compareHtml ? ' ' + compareHtml : ''}`;
    }

    // Update Title
    const titleElement = this.querySelector('.product-sticky-form__title');
    if (titleElement) {
      const productTitle = this.productData.title;
      const variantTitle = this._filterSizeTitle(this.currentVariant.title, this._activeSizeTab);
      titleElement.textContent = variantTitle && variantTitle !== 'Default Title'
        ? `${productTitle} - ${variantTitle}` : productTitle;
    }

    // Update Add to Cart Button
    if (this.stickyAddToCartButton) {
        if (this.currentVariant.available) {
            this.stickyAddToCartButton.disabled = false;
            this.stickyAddToCartButton.textContent = 'Add to Cart';
            this.stickyAddToCartButton.classList.remove('button--ternary');
            this.stickyAddToCartButton.classList.add('button--primary');
        } else {
            this.stickyAddToCartButton.disabled = true;
            this.stickyAddToCartButton.textContent = 'Sold Out';
            this.stickyAddToCartButton.classList.add('button--ternary');
            this.stickyAddToCartButton.classList.remove('button--primary');
        }
    }
  }

  _digits(s) { return (s || '').replace(/[^0-9.]/g, '').trim(); }
  _isSizeToken(s) {
    const t = (s || '').trim();
    return /^(?:US\s*)?(?:M|W|MEN|WOMEN)\b/i.test(t) && /\d/.test(t);
  }
  _extractColorAndSize(title) {
    const idx = title.indexOf(' / ');
    if (idx !== -1) {
      const color = title.slice(0, idx).trim();
      const sizePart = title.slice(idx + 3).trim();
      return { color, sizePart };
    }
    return { color: '', sizePart: title.trim() };
  }
  _selectSizeFromParts(parts, tab) {
    const ps = (parts || []).map(p => (p || '').trim());
    const normalize = (raw, t) => {
      const r = (raw || '').trim();
      const num = r.replace(/[^0-9.]/g, '').trim();
      if (!num) return '';
      if (/^W/i.test(r)) return `W ${num}`;
      if (/^M/i.test(r)) return `M ${num}`;
      return t === 'us-women' ? `W ${num}` : `M ${num}`;
    };
    const pickWomen = ps.find(p => /^(?:US\s*)?(?:W|WOMEN)\b/i.test(p) && /\d/.test(p));
    const pickMen = ps.find(p => /^(?:US\s*)?(?:M|MEN)\b/i.test(p) && /\d/.test(p));
    if (tab === 'us-women' && pickWomen) return normalize(pickWomen, tab);
    if (tab === 'us-men' && pickMen) return normalize(pickMen, tab);
    const any = pickWomen || pickMen || ps.find(p => /\d/.test(p)) || '';
    return normalize(any, tab);
  }
  _filterSizeTitle(title, tab) {
    if (!title) return title;
    const { color, sizePart } = this._extractColorAndSize(title);
    let filteredSize = sizePart;
    if (sizePart.indexOf('|') !== -1) {
      filteredSize = this._selectSizeFromParts(sizePart.split('|'), tab) || sizePart;
    }
    if (color && !this._isSizeToken(color) && !/\d/.test(color)) {
      return `${color} / ${filteredSize}`;
    }
    return filteredSize;
  }

  _syncSizeOptionLabels() {
    const tab = this._activeSizeTab || 'us-women';
    this.optionSelectors.forEach(select => {
      Array.from(select.options).forEach(opt => {
        const v = opt.value || '';
        if (v.indexOf('|') === -1) return;
        const parts = v.split('|');
        const menPart = parts.find(p => p.trim().toUpperCase().startsWith('M'));
        const womenPart = parts.find(p => p.trim().toUpperCase().startsWith('W'));
        const normalize = (raw) => {
          const r = (raw || '').trim();
          const num = r.replace(/[^0-9.]/g, '').trim();
          if (!num) return r;
          if (/^W/i.test(r)) return `W ${num}`;
          if (/^M/i.test(r)) return `M ${num}`;
          return r;
        };
        let text = '';
        if (tab === 'us-women' && womenPart) {
          text = normalize(womenPart);
        } else if (tab === 'us-men' && menPart) {
          text = normalize(menPart);
        } else if (womenPart || menPart) {
          text = normalize(womenPart || menPart);
        } else {
          text = v;
        }
        opt.textContent = text;
      });
    });
  }

  _applyOptions(options) {
    if (!Array.isArray(options)) return;
    options.forEach((val, idx) => {
      if (!val) return;
      const select = this.querySelector(`select[data-option-index="${idx}"]`);
      if (!select) return;
      const match = Array.from(select.options).find(o => o.value === val);
      if (match) {
        select.value = val;
        match.selected = true;
      }
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  _onScroll() {
    this._updateVisibility();
  }

  _onResize() {
    this._firstScreenHeight = window.innerHeight;
    this._updateVisibility();
  }

  _setupVisibilityObserver() {
    const mainButton = document.querySelector('.product__main__content .btn--add-to-cart');
    if (mainButton) {
      this.observer = new window.IntersectionObserver((entries) => {
        const entry = entries[0];
        this._isMainButtonVisible = entry.isIntersecting;
        this._updateVisibility();
      }, { threshold: 0 });
      this.observer.observe(mainButton);
    }
    const footer = document.querySelector('.shopify-section-group-group-footer');
    if (footer) {
      this.footerObserver = new window.IntersectionObserver((entries) => {
        const entry = entries[0];
        this._isFooterVisible = entry.intersectionRatio > 0;
        this._updateVisibility();
      }, { threshold: 0 });
      this.footerObserver.observe(footer);
    }
  }

  _updateVisibility() {
    if (
      window.scrollY < this._firstScreenHeight ||
      this._isMainButtonVisible ||
      this._isFooterVisible
    ) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }
  }

  handleStickyAddToCart(event) {
    event.preventDefault();
    if (!this.currentVariant || !this.currentVariant.id) return;
    const variantId = this.currentVariant.id;

    this.stickyAddToCartButton.disabled = true;
    this.stickyAddToCartButton.classList.add('loading');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(response => response.json())
    .then(() => {
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      } else {
        // Fallback if refreshCart is not available
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.open();
        } else {
          window.location.href = '/cart';
        }
      }
    })
    .catch((e) => { 
        console.error(e);
        alert('Add to cart error'); 
    })
    .finally(() => {
      this.stickyAddToCartButton.disabled = false;
      this.stickyAddToCartButton.classList.remove('loading');
    });
  }
}

customElements.define('product-sticky-form', ProductStickyForm);